import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Link } from '@/router';
import { MENU } from '@/data/site';
import { SongketDivider } from '@/components/Motifs';

const levelStyle = (level) => {
  if (level.includes('Pedas')) return 'bg-chili/10 text-chili';
  if (level.includes('Segar')) return 'bg-leaf/10 text-leaf';
  return 'bg-turmeric/10 text-turmeric-dark';
};

export default function MenuPage() {
  const [q, setQ] = useState('');
  const qn = q.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!qn) return MENU;
    return MENU.map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (it) => it.name.toLowerCase().includes(qn) || it.desc.toLowerCase().includes(qn)
      ),
    })).filter((cat) => cat.items.length > 0);
  }, [qn]);

  return (
    <div data-testid="menu-page">
      <section className="border-b border-stone-200 bg-cream-2">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-20">
          <nav className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500" aria-label="Breadcrumb" data-testid="menu-breadcrumb">
            <Link to="/" className="text-crimson hover:underline">Beranda</Link>
            <span className="mx-2" aria-hidden="true">›</span>
            <span aria-current="page">Menu</span>
          </nav>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
            Daftar Hidangan
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-stone-600 sm:text-lg">
            Hidangan khas Minangkabau yang akrab di meja rumah makan Padang — dari lauk daging
            yang dimasak berjam-jam hingga sambal yang diulek segar. Sesuai tradisi hidang,
            harga menyesuaikan piring lauk yang Anda pilih di tempat.
          </p>
          <form role="search" onSubmit={(e) => e.preventDefault()} className="mt-8 max-w-md">
            <label htmlFor="menu-search" className="sr-only">Cari hidangan</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400" aria-hidden="true" />
              <input
                id="menu-search"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari hidangan… mis. rendang"
                data-testid="menu-search-input"
                className="w-full rounded-full border border-stone-300 bg-white py-3.5 pl-12 pr-5 text-sm text-charcoal shadow-sm transition-colors duration-200 placeholder:text-stone-400 focus:border-turmeric focus:outline-none"
              />
            </div>
          </form>
        </div>
        <SongketDivider />
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-20">
        {filtered.length === 0 && (
          <p className="rounded-2xl border border-stone-200 bg-white p-8 text-center text-sm text-stone-600" data-testid="menu-empty-state">
            Tidak ada hidangan yang cocok dengan pencarian “{q}”. Coba kata kunci lain.
          </p>
        )}
        <div className="space-y-16">
          {filtered.map((cat, ci) => (
            <section key={cat.category} aria-labelledby={`kat-${ci}`} data-testid={`menu-category-${ci}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3 border-b-2 border-turmeric/60 pb-4">
                <h2 id={`kat-${ci}`} className="font-display text-2xl font-bold tracking-tight text-charcoal sm:text-3xl">
                  {cat.category}
                </h2>
                <p className="text-sm italic text-stone-500">{cat.note}</p>
              </div>
              <ul className="mt-8 grid gap-x-12 gap-y-8 md:grid-cols-2">
                {cat.items.map((it) => (
                  <li key={it.slug} data-testid={`menu-item-${it.slug}`} className="group">
                    <div className="flex items-baseline gap-3">
                      <h3 className="shrink-0 text-lg font-bold text-charcoal transition-colors duration-200 group-hover:text-crimson">
                        {it.name}
                      </h3>
                      <span className="menu-leader h-3 min-w-6 flex-1" aria-hidden="true" />
                      <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold ${levelStyle(it.level)}`}>
                        {it.level}
                      </span>
                    </div>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-stone-600">
                      {it.badge && (
                        <span className="mr-2 inline-block rounded-full bg-crimson px-2.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider text-santan">
                          {it.badge}
                        </span>
                      )}
                      {it.desc}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-20 rounded-3xl border border-turmeric/40 bg-turmeric-soft p-8 md:p-10" data-testid="menu-note">
          <h2 className="font-display text-2xl font-bold text-charcoal">Tentang Harga</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone-700">
            Sesuai tradisi rumah makan Padang, harga dihitung dari hidangan yang benar-benar
            Anda santap di meja. Jangan sungkan bertanya kepada pelayan sebelum memilih lauk —
            kami dengan senang hati membantu.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <Link
              to="/faq/"
              data-testid="menu-cta-faq"
              className="inline-flex items-center rounded-full bg-crimson px-6 py-3 text-sm font-bold text-santan transition-colors duration-200 hover:bg-crimson-dark"
            >
              Baca Pertanyaan Umum
            </Link>
            <Link
              to="/tentang/"
              data-testid="menu-cta-tentang"
              className="inline-flex items-center rounded-full border-2 border-charcoal/15 bg-white px-6 py-3 text-sm font-bold text-charcoal transition-colors duration-200 hover:border-turmeric hover:text-crimson"
            >
              Kenali Tradisi Hidang
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
